// components/DateInput.tsx
import React from 'react';
import { TextField, Box, Typography } from '@mui/material';
import { DateInputProps, isSingleMode } from '../../models/date.types';

const DateInput: React.FC<DateInputProps> = (props) => {
    const {
        mode,
        type,
        labels = {
            single: 'Date',
            start: 'From',
            end: 'To'
        },
        disabled = false,
        required = false,
        minDate,
        maxDate,
        showTimeZone = true,
        className = '',
        validateRange = true
    } = props;

    // Get input type based on mode
    const inputType = type === 'datetime' ? 'datetime-local' : 'date';

    // Format date for input
    const formatForInput = (dateString?: string): string => {
        if (!dateString) return '';

        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';

            if (type === 'date') {
                // For date: YYYY-MM-DD
                return date.toISOString().split('T')[0];
            } else {
                // For datetime: YYYY-MM-DDTHH:mm
                return new Date(dateString).toISOString().slice(0, 16);
            }
        } catch {
            return '';
        }
    };

    // Render timezone indicator
    const renderTimezone = () => {
        if (!showTimeZone || type === 'date') return null;

        return (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Times are in your local timezone
            </Typography>
        );
    };

    // Render single date input
    const renderSingleDate = () => {
        if (!isSingleMode(props)) return null;

        const { value, onChange } = props;

        return (
            <Box>
                <TextField
                    label={labels.single}
                    type={inputType}
                    value={formatForInput(value)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        onChange(e.target.value);
                    }}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    disabled={disabled}
                    required={required}
                    margin="normal"
                    inputProps={{
                        min: minDate ? formatForInput(minDate) : undefined,
                        max: maxDate ? formatForInput(maxDate) : undefined
                    }}
                />
                {renderTimezone()}
            </Box>
        );
    };

    // Render date range inputs
    const renderDateRange = () => {
        if (isSingleMode(props)) return null;

        const { value, onChange } = props;
        const { startDate, endDate } = value;

        return (
            <Box>
                <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
                    <Box flex={1}>
                        <TextField
                            label={labels.start}
                            type={inputType}
                            value={formatForInput(startDate)}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const newStartDate = e.target.value;
                                onChange({
                                    startDate: newStartDate,
                                    endDate
                                });
                            }}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            disabled={disabled}
                            required={required}
                            margin="normal"
                            inputProps={{
                                min: minDate ? formatForInput(minDate) : undefined,
                                max: endDate && validateRange ? formatForInput(endDate) : undefined
                            }}
                        />
                    </Box>

                    <Box flex={1}>
                        <TextField
                            label={labels.end}
                            type={inputType}
                            value={formatForInput(endDate)}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const newEndDate = e.target.value;
                                onChange({
                                    startDate,
                                    endDate: newEndDate
                                });
                            }}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            disabled={disabled}
                            required={required}
                            margin="normal"
                            inputProps={{
                                min: startDate && validateRange ? formatForInput(startDate) : minDate ? formatForInput(minDate) : undefined,
                                max: maxDate ? formatForInput(maxDate) : undefined
                            }}
                        />
                    </Box>
                </Box>
                {renderTimezone()}

                {/* Validation error if end date is before start date */}
                {validateRange && startDate && endDate && new Date(endDate) < new Date(startDate) && (
                    <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                        End date must be after start date
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <Box className={className}>
            {mode === 'single' ? renderSingleDate() : renderDateRange()}
        </Box>
    );
};

export default DateInput;